import { useGetList, useRecordContext } from "ra-core";
import { Link } from "react-router";
import { CreditCard, ExternalLink } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Chip } from "../shared/Chip";
import type { Contact, Subscription } from "../types";
import { formatCurrency } from "../finance/treasuryFormat";

// Sprint 9.4 — Bandeau "Abonnement actuel" en tête du tab Payments.
// Source : subscriptions filtrées par company_id du contact.
// Statuts active/trialing → vert ; canceled/past_due → rouge/orange.

const STATUS_VARIANT = {
  active: "green",
  trialing: "green",
  canceled: "red",
  past_due: "red",
  incomplete: "orange",
} as const;

const STATUS_LABEL: Record<string, string> = {
  active: "Actif",
  trialing: "Essai",
  canceled: "Annulé",
  past_due: "En retard",
  incomplete: "Incomplet",
};

export function PaymentsSubBanner() {
  const record = useRecordContext<Contact>();
  const { data, isPending } = useGetList<Subscription>(
    "subscriptions",
    {
      pagination: { page: 1, perPage: 5 },
      sort: { field: "updated_at", order: "DESC" },
      filter: { company_id: record?.company_id },
    },
    { enabled: !!record?.company_id },
  );

  if (!record?.company_id) return null;
  if (isPending) return <Skeleton className="h-16 w-full" />;
  if (!data?.length) return null;

  // Préférence : un abonnement actif si dispo, sinon le plus récent.
  const sub =
    data.find((s) => s.status === "active" || s.status === "trialing") ??
    data[0];
  const amount =
    sub.amount == null
      ? null
      : formatCurrency(sub.amount, sub.currency ?? "eur");
  const variant =
    STATUS_VARIANT[sub.status as keyof typeof STATUS_VARIANT] ?? "gray";

  return (
    <Card>
      <CardContent className="py-3 md:py-3.5">
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-lg bg-fabrik-green-soft text-fabrik-accent-text flex items-center justify-center shrink-0">
            <CreditCard className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[14px] font-semibold text-foreground truncate">
                {sub.product_name ?? "Abonnement"}
              </p>
              <Chip variant={variant}>
                {STATUS_LABEL[sub.status] ?? sub.status}
              </Chip>
            </div>
            {amount && sub.recurring_interval && (
              <p className="text-[12px] text-fabrik-text-3 mt-0.5">
                {amount} / {sub.recurring_interval === "month" ? "mois" : "an"}
              </p>
            )}
          </div>
          <Link
            to={`/companies/${sub.company_id}/show`}
            className="text-fabrik-text-3 hover:text-foreground shrink-0"
            title="Voir l'entreprise"
          >
            <ExternalLink className="size-4" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
